import * as React from 'react';

/**
 * Marks <html> as the CRM shell (or the trader desktop shell, which shares
 * the colour and surface tokens but none of the CRM density) so the token set in index.css
 * (`html[data-shell="crm"]`) applies, including to Radix portals under
 * <body>. Removed on unmount, so the trader portal and the auth pages keep
 * their own look.
 */
export function useCrmShellAttribute(enabled = true, shell: 'crm' | 'trader' = 'crm') {
    React.useLayoutEffect(() => {
        if (!enabled) return;
        const root = document.documentElement;
        root.dataset.shell = shell;
        return () => {
            delete root.dataset.shell;
        };
    }, [enabled, shell]);
}

function useMediaQuery(query: string) {
    const subscribe = React.useCallback(
        (onChange: () => void) => {
            const list = window.matchMedia(query);
            list.addEventListener('change', onChange);
            return () => list.removeEventListener('change', onChange);
        },
        [query]
    );
    return React.useSyncExternalStore(
        subscribe,
        () => window.matchMedia(query).matches,
        () => false
    );
}

// Same key the previous layout used, so a user's choice carries over.
const COLLAPSED_KEY = 'crm-sidebar-minimized';

/**
 * Sidebar collapsed state. On desktop (lg+) it is the user's choice,
 * remembered per browser. On tablets (md to lg) the sidebar is always the
 * icon rail so the top bar keeps room for search; the toggle is hidden
 * there (`canToggle`).
 */
export function useSidebarCollapsed() {
    const [preference, setPreference] = React.useState<boolean>(() => {
        try {
            return localStorage.getItem(COLLAPSED_KEY) === 'true';
        } catch {
            return false;
        }
    });
    const isDesktop = useMediaQuery('(min-width: 1024px)');

    const toggle = React.useCallback(() => {
        setPreference(previous => {
            const next = !previous;
            try {
                localStorage.setItem(COLLAPSED_KEY, String(next));
            } catch {
                // Not persisted; still toggles for this session.
            }
            return next;
        });
    }, []);

    return { collapsed: preference || !isDesktop, canToggle: isDesktop, toggle };
}
